import { Clock, FileText, TrendingUp, Users } from "lucide-react";
import { useGetIdentity, useGetList, useTranslate } from "ra-core";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { useIsMobile } from "@/hooks/use-mobile";

import { FilterCategory } from "../filters/FilterCategory";
import { ResponsiveFilters } from "../misc/ResponsiveFilters";
import type { Sale } from "../types";
import { DateRangeFilter } from "./DateRangeFilter";
import { useActiveFilterChips } from "../misc/ActiveFilterBar";
import {
  CONTENT_OPTIONS,
  HOST_KEYS,
  STATUS_OPTIONS,
  useZoomMeetingFilterBar,
} from "./zoomMeetingFilterBar";

const BUTTON_CLASSES = "w-auto md:w-full justify-between h-10 md:h-8";

export const ZoomMeetingListFilter = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const size = isMobile ? "lg" : undefined;
  const { identity } = useGetIdentity();
  const { data: sales } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "first_name", order: "ASC" },
    filter: { "disabled@neq": true },
  });
  const { count, clearAll } = useActiveFilterChips(useZoomMeetingFilterBar());

  return (
    <ResponsiveFilters
      activeFilters={{ count, clearAll }}
      searchInput={{
        placeholder: translate("resources.zoom_meetings.filters.search"),
      }}
    >
      <FilterCategory
        icon={<Users className="h-4 w-4" />}
        label="resources.zoom_meetings.filters.host"
      >
        {identity && (
          // Hosted or attended. `@cs` takes the Postgres array literal as a
          // string; FakeRest's contains filter rejects anything else.
          <ToggleFilterButton
            className={BUTTON_CLASSES}
            size={size}
            label="resources.zoom_meetings.filters.my_meetings"
            value={{
              ...HOST_KEYS,
              "attendee_sales_ids@cs": `{${identity.id}}`,
            }}
          />
        )}
        {sales
          ?.filter((sale) => sale.id !== identity?.id)
          .map((sale) => (
            <ToggleFilterButton
              key={sale.id}
              className={BUTTON_CLASSES}
              size={size}
              label={`${sale.first_name} ${sale.last_name}`.trim()}
              value={{ ...HOST_KEYS, "host_sales_id@eq": sale.id }}
            />
          ))}
        {/* Office Hours and other meetings with no host, or a host who is not
          a CRM user. */}
        <ToggleFilterButton
          className={BUTTON_CLASSES}
          size={size}
          label="resources.zoom_meetings.filters.no_host"
          value={{ ...HOST_KEYS, "host_sales_id@is": "null" }}
        />
      </FilterCategory>

      <FilterCategory
        icon={<Clock className="h-4 w-4" />}
        label="resources.zoom_meetings.fields.meeting_start_time"
      >
        <DateRangeFilter
          className="h-10 md:h-8"
          size={isMobile ? "lg" : "sm"}
        />
      </FilterCategory>

      <FilterCategory
        icon={<FileText className="h-4 w-4" />}
        label="resources.zoom_meetings.fields.content"
      >
        {CONTENT_OPTIONS.map((option) => (
          <ToggleFilterButton
            key={option.label}
            className={BUTTON_CLASSES}
            size={size}
            label={option.label}
            value={option.value}
          />
        ))}
      </FilterCategory>

      <FilterCategory
        icon={<TrendingUp className="h-4 w-4" />}
        label="resources.zoom_meetings.fields.status"
      >
        {STATUS_OPTIONS.map((option) => (
          <ToggleFilterButton
            key={option.label}
            className={BUTTON_CLASSES}
            size={size}
            label={option.label}
            value={option.value}
          />
        ))}
      </FilterCategory>
    </ResponsiveFilters>
  );
};
